import { useEffect, useState } from 'react';
import type { ProviderOtpPrompt } from '@asko/shared';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * Scoring o'rtasida provayder so'ragan kod (PLAN.md §0.0.1).
 *
 * Shartnoma OTP'sidan farqi: bu yerda scoring hali tugamagan. Ikkinchi
 * provayder o'z ishini davom ettiraveradi, shuning uchun forma alohida
 * ekran emas — kod so'ragan provayder kartochkasining ichida turadi.
 */

interface ResultOtpProps {
  otp: ProviderOtpPrompt;
  onConfirm: (code: string) => void;
  onResend: () => void;
  busy: 'confirm' | 'resend' | null;
  error: ApiError | null;
}

export function ResultOtp({ otp, onConfirm, onResend, busy, error }: ResultOtpProps) {
  const { t, apiMessage } = useI18n();
  const [code, setCode] = useState('');
  const secondsLeft = useCountdown(otp.expiresAt);

  const attemptsLeft = otp.maxAttempts - otp.attempts;
  const disabled = busy !== null;

  return (
    <div className="result__otp">
      <h4 className="result__otp-title">{t('result.otpTitle')}</h4>
      <p className="result__otp-hint">
        {otp.phoneMask
          ? t('result.otpHint', { phone: otp.phoneMask })
          : t('result.otpHintNoPhone')}
      </p>

      {otp.mockCode && (
        <p className="phone-verify__hint">
          <span className="phone-verify__mock">{t('phone.mock', { code: otp.mockCode })}</span>
        </p>
      )}

      {/* Muddat — provayder aytgan baho. Tugagach kiritishni taqiqlamaymiz:
          kodni qabul qilish yoki qilmaslik provayderning ishi. */}
      <p className="result__otp-timer">
        {secondsLeft > 0
          ? t('result.otpTimer', { time: formatLeft(secondsLeft) })
          : t('result.otpMaybeExpired')}
      </p>

      {/* Vaqt tugagach eski kodni kiritishning ma'nosi yo'q: Alif uni
          qabul qilmaydi. Buni aytib qo'yamiz, aks holda sotuvchi
          mijozdan o'sha raqamlarni qayta-qayta so'rab o'tiradi. */}
      {secondsLeft === 0 && (
        <p className="result__otp-hint">{t('result.otpExpiredHint')}</p>
      )}

      <div className="phone-verify">
        <input
          className="field__input field__input--mono"
          inputMode="numeric"
          placeholder={t('phone.codePlaceholder')}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          aria-label={t('phone.codeLabel')}
          aria-invalid={Boolean(error)}
          disabled={disabled}
        />
        <button
          className="btn btn--primary"
          type="button"
          onClick={() => onConfirm(code)}
          disabled={disabled || code.length < 4}
        >
          {busy === 'confirm' ? t('result.otpConfirming') : t('result.otpConfirm')}
        </button>
      </div>

      {error && (
        <div className="alert alert--error" role="alert">
          {apiMessage(error)}
          {attemptsLeft > 0 && <div>{t('result.otpAttempts', { left: attemptsLeft })}</div>}
        </div>
      )}

      {/* Alif hujjati: noto'g'ri koddan keyin yangi kod so'rash kerak —
          eskisini qayta kiritish ishlamasligi mumkin. */}
      {otp.attempts > 0 && <p className="result__otp-hint">{t('result.otpAskNew')}</p>}

      {/*
        Yangi kodni faqat hisoblagich tugagach so'rash mumkin.
        Alif ikki marta yuborish orasida bir daqiqa tanaffus talab
        qiladi («Подождите 1 минуту перед повторным запросом»), kod esa
        aynan shuncha yashaydi — ya'ni hisoblagich nolga yetganda
        tanaffus ham tugagan bo'ladi. Ilgari tugma doim ochiq turardi
        va erta bosilsa provayder xato qaytarardi.
      */}
      <div className="result__otp-resend">
        <button
          className="btn btn--ghost btn--small"
          type="button"
          onClick={onResend}
          disabled={disabled || otp.resendsLeft === 0 || secondsLeft > 0}
        >
          {busy === 'resend' ? t('result.otpResending') : t('result.otpResend')}
        </button>
        {secondsLeft > 0 ? (
          <span className="result__otp-left">
            {t('result.otpResendIn', { seconds: secondsLeft })}
          </span>
        ) : (
          otp.resendsLeft > 0 && (
            <span className="result__otp-left">
              {t('result.otpResendsLeft', { n: otp.resendsLeft })}
            </span>
          )
        )}
      </div>
    </div>
  );
}

/** Har soniyada yangilanadigan qolgan vaqt. */
function useCountdown(expiresAt: string): number {
  const target = new Date(expiresAt).getTime();
  const [now, setNow] = useState(() => Date.now());
  const finished = now >= target;

  useEffect(() => {
    // Vaqt tugagach taymerni to'xtatamiz — fonda behuda ishlashning hojati yo'q.
    if (finished) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [finished]);

  return Math.max(0, Math.ceil((target - now) / 1000));
}

function formatLeft(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
